import React, { useState, useEffect } from 'react';
import { Gallery } from '../../types/Gallery';
import { BlogPost } from '../../types/Blog';
import sitePaths from '../../utils/sitePaths';
import HelpButton from '../Admin/HelpButton';

interface HealthIssue {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  page?: string;
  fixable: boolean;
  fixAction?: () => void;
}

interface SEOHealthCheckerProps {
  galleries: Gallery[];
  blogPosts: BlogPost[];
  onFix?: (issueId: string) => void;
}

const SEOHealthChecker: React.FC<SEOHealthCheckerProps> = ({ galleries, blogPosts, onFix }) => {
  const [issues, setIssues] = useState<HealthIssue[]>([]);
  const [isChecking, setIsChecking] = useState(false);

  const runHealthCheck = () => {
    setIsChecking(true);
    const foundIssues: HealthIssue[] = [];

    // Check for missing alt text in galleries
    galleries.forEach(gallery => {
      const missingAltCount = gallery.images?.filter(img => !img.alt || img.alt.trim() === '').length || 0;
      if (missingAltCount > 0) {
        foundIssues.push({
          id: `gallery-${gallery.id}-alt`,
          severity: 'warning',
          title: `Missing Alt Text in "${gallery.name}"`,
          description: `${missingAltCount} image${missingAltCount > 1 ? 's' : ''} missing alt text`,
          page: sitePaths.galleryPath(gallery),
          fixable: true,
          fixAction: () => {
            // Auto-generate alt text from filenames
            console.log(`Fixing alt text for gallery ${gallery.id}`);
            if (onFix) onFix(`gallery-${gallery.id}-alt`);
          }
        });
      }

      // Check for missing descriptions
      if (!gallery.description || gallery.description.trim() === '') {
        foundIssues.push({
          id: `gallery-${gallery.id}-desc`,
          severity: 'warning',
          title: `Missing Description for "${gallery.name}"`,
          description: 'Gallery description is missing',
          page: sitePaths.galleryPath(gallery),
          fixable: false
        });
      }
    });

    // Check blog posts
    blogPosts.forEach(post => {
      if (!post.excerpt || post.excerpt.trim() === '') {
        foundIssues.push({
          id: `blog-${post.id}-excerpt`,
          severity: 'warning',
          title: `Missing Excerpt for "${post.title}"`,
          description: 'Blog post excerpt is missing',
          page: `/blog/${post.slug}`,
          fixable: true,
          fixAction: () => {
            // Auto-generate excerpt from content
            const excerpt = post.content.substring(0, 160).replace(/<[^>]*>/g, '');
            console.log(`Fixing excerpt for blog ${post.id}`);
            if (onFix) onFix(`blog-${post.id}-excerpt`);
          }
        });
      }
    });

    // Check for duplicate titles (simplified check)
    const titles = new Map<string, string[]>();
    galleries.forEach(g => {
      if (g.name) {
        if (!titles.has(g.name)) titles.set(g.name, []);
        titles.get(g.name)!.push(`Gallery: ${g.id}`);
      }
    });

    titles.forEach((locations, title) => {
      if (locations.length > 1) {
        foundIssues.push({
          id: `duplicate-title-${title}`,
          severity: 'info',
          title: `Potential Duplicate Title: "${title}"`,
          description: `Found in ${locations.length} locations`,
          fixable: false
        });
      }
    });

    setIssues(foundIssues);
    setIsChecking(false);
  };

  useEffect(() => {
    runHealthCheck();
  }, [galleries, blogPosts]);

  const criticalIssues = issues.filter(i => i.severity === 'critical');
  const warningIssues = issues.filter(i => i.severity === 'warning');
  const infoIssues = issues.filter(i => i.severity === 'info');

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return 'fas fa-exclamation-circle';
      case 'warning': return 'fas fa-exclamation-triangle';
      case 'info': return 'fas fa-info-circle';
      default: return 'fas fa-circle';
    }
  };

  return (
    <div className="seo-health-checker">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <h3>SEO Health Check</h3>
          <HelpButton section="seo-health" tooltip="SEO Health Check Help" />
        </div>
        <button
          className="btn btn-secondary"
          onClick={runHealthCheck}
          disabled={isChecking}
        >
          <i className="fas fa-sync-alt"></i> {isChecking ? 'Checking...' : 'Run Check'}
        </button>
      </div>

      <div className="health-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ padding: '1rem', background: '#fee2e2', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc2626' }}>{criticalIssues.length}</div>
          <div style={{ fontSize: '0.875rem', color: '#991b1b' }}>Critical Issues</div>
        </div>
        <div style={{ padding: '1rem', background: '#fef3c7', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>{warningIssues.length}</div>
          <div style={{ fontSize: '0.875rem', color: '#92400e' }}>Warnings</div>
        </div>
        <div style={{ padding: '1rem', background: '#dbeafe', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>{infoIssues.length}</div>
          <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>Info</div>
        </div>
      </div>

      <div className="health-issues-list">
        {issues.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#16a34a' }}>
            <i className="fas fa-check-circle" style={{ fontSize: '3rem', marginBottom: '1rem' }}></i>
            <div style={{ fontSize: '1.25rem', fontWeight: '600' }}>All Good!</div>
            <div>No SEO issues found.</div>
          </div>
        ) : (
          issues.map(issue => (
            <div key={issue.id} className={`health-issue ${issue.severity}`}>
              <div className="health-issue-icon">
                <i className={getSeverityIcon(issue.severity)}></i>
              </div>
              <div className="health-issue-content">
                <div className="health-issue-title">{issue.title}</div>
                <div className="health-issue-description">{issue.description}</div>
                {issue.page && (
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Page: {issue.page}
                  </div>
                )}
              </div>
              {issue.fixable && issue.fixAction && (
                <div className="health-issue-actions">
                  <button
                    className="btn btn-primary"
                    onClick={issue.fixAction}
                    style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                  >
                    <i className="fas fa-magic"></i> Fix
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SEOHealthChecker;

